import React from 'react'
import map from 'lodash/map'
import isEmpty from 'lodash/isEmpty'
import type { User } from '@ztechapps/common.contract'
import { AvatarGroup, HorizontalStack } from '@ztechapps/webui.util'
import { MembersSelect } from '@ztechapps/webui.input'

const MAX_AVATARS = 3

// react-select dedupes options by the value returned here. Template-task members
// are stored as full User objects without a `.value` field, so key by `_id`.
// Typed `unknown` because MembersSelect's react-select option type is the generic
// SelectOption, not User.
const getMemberOptionValue = (member: unknown) => (member as User)?._id ?? ''

const buildMemberAvatars = (members?: User[]) =>
  map(members, (member) => {
    let picture = member?.profile?.picture ?? ''
    if (picture.includes('/dft-customer.jpg')) picture = ''
    return { name: member?.profile?.name, src: picture }
  })

export type MembersBadgeProps = {
  members?: User[]
  className?: string
}

export const MembersBadge = ({ members, className }: MembersBadgeProps) => {
  if (isEmpty(members)) return null

  const avatars = buildMemberAvatars(members)
  const overflowCount = avatars.length - MAX_AVATARS

  return (
    <HorizontalStack gap={1} className={`align-items-center flex-shrink-0 ${className ?? ''}`}>
      <AvatarGroup dataArray={avatars} size={20} max={MAX_AVATARS} />
      {overflowCount > 0 && <small className="fw-semibold text-secondary">+{overflowCount}</small>}
    </HorizontalStack>
  )
}

export type TaskTemplateMembersEditorProps = {
  members?: User[]
  onChange: (members: User[]) => void
  disabled?: boolean
}

/**
 * Controlled multi-user selector for a template task's default members.
 * Mirrors the inline-controlled mode of TaskStatusActionsEditor: holds no
 * persistence of its own, just lifts the selected users to the parent.
 */
export const TaskTemplateMembersEditor = ({ members, onChange, disabled }: TaskTemplateMembersEditorProps) => {
  return (
    <MembersSelect
      value={(members ?? []) as never}
      getOptionValue={getMemberOptionValue}
      onChange={(selected: unknown) => onChange((selected as User[]) ?? [])}
      isDisabled={disabled}
      isClearable
    />
  )
}
